import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { Trophy, X, RefreshCw } from "lucide-react";
import { useCountUp } from "@/hooks/useCountUp";
import { TOURNAMENT } from "./homeData";

function Contender({ item, run, isWinner, eliminated, reduce }) {
  const score = useCountUp(item.score, run, { duration: 1400, reduce });
  return (
    <motion.div
      animate={{
        opacity: eliminated ? 0.4 : 1,
        scale: isWinner ? 1.04 : eliminated ? 0.97 : 1,
        y: isWinner ? -6 : 0,
      }}
      transition={{ type: "spring", stiffness: 200, damping: 22 }}
      className={`relative rounded-2xl p-6 border transition-colors duration-500 ${
        isWinner
          ? "bg-donkey-orange/10 border-donkey-orange shadow-[0_0_40px_rgba(249,115,22,0.3)]"
          : "card-donkey"
      }`}
      data-testid={`tournament-contender${isWinner ? "-winner" : ""}`}
    >
      {isWinner && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-3 py-1 rounded-full bg-donkey-orange text-donkey-black font-bold">
          <Trophy className="w-3 h-3" /> Winner
        </span>
      )}
      {eliminated && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-donkey-red/50 text-donkey-red bg-donkey-red/10">
          <X className="w-3 h-3" /> Out
        </span>
      )}
      <p className={`font-heading font-semibold ${isWinner ? "text-donkey-warm" : "text-donkey-warm/90"}`}>{item.name}</p>
      <div className="mt-4 flex items-end justify-between">
        <span className="text-[11px] uppercase tracking-wider text-donkey-grey">Fit score</span>
        <span className="font-mono text-2xl" style={{ color: isWinner ? "#F97316" : "#A8A29E" }}>{score}</span>
      </div>
      <div className="mt-2 h-2 rounded-full bg-donkey-line/70 overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: isWinner ? "#F97316" : "#57534e" }}
          initial={{ width: 0 }}
          animate={{ width: run ? `${item.score}%` : 0 }}
          transition={{ duration: 1.4, ease: "easeOut" }}
        />
      </div>
      <p className="mt-3 text-xs text-donkey-grey leading-snug">{item.note}</p>
    </motion.div>
  );
}

export function OpportunityTournament() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.4, once: false });
  const [run, setRun] = useState(false);
  const [decided, setDecided] = useState(false);

  const winnerIdx = TOURNAMENT.reduce((best, it, i, arr) => (it.score > arr[best].score ? i : best), 0);

  useEffect(() => {
    if (reduce) {
      setRun(true);
      setDecided(true);
      return;
    }
    if (inView) {
      setRun(true);
      const id = setTimeout(() => setDecided(true), 1900);
      return () => clearTimeout(id);
    }
    setRun(false);
    setDecided(false);
  }, [inView, reduce]);

  const replay = () => {
    setDecided(false);
    setRun(false);
    setTimeout(() => setRun(true), 60);
    setTimeout(() => setDecided(true), 1900);
  };

  return (
    <section ref={ref} className="relative overflow-hidden border-t border-donkey-line bg-donkey-black">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="w-16 h-1 rounded-full bg-donkey-orange mb-6" />
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-donkey-warm">Three opportunities. One winner.</h2>
            <p className="text-donkey-grey mt-4 max-w-2xl leading-relaxed">
              Your options don't just get listed &mdash; they compete. The Donkey scores each on fit, demand and reach, then makes the weaker ones earn their place.
            </p>
          </div>
          <button
            onClick={replay}
            data-testid="tournament-replay"
            className="inline-flex items-center gap-2 text-sm px-4 h-10 rounded-full border border-donkey-line text-donkey-warm hover:border-donkey-orange hover:text-donkey-orange transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Run it again
          </button>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-5" data-testid="tournament-grid">
          {TOURNAMENT.map((item, i) => (
            <Contender
              key={item.name}
              item={item}
              run={run}
              reduce={reduce}
              isWinner={decided && i === winnerIdx}
              eliminated={decided && i !== winnerIdx}
            />
          ))}
        </div>
        <p className="mt-6 text-xs text-donkey-grey/70">Illustrative comparison &mdash; your real opportunities are built from your interview.</p>
      </div>
    </section>
  );
}

export default OpportunityTournament;
